import React from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import SectionHelp from './SectionHelp';
import '../styles/ColorsSection.css';

const ColorsSection = () => {
  const {
    colorNames,
    setColorNames,
    colorRows,
    updateColorRow,
    updateColorPercentage,
    addColor,
    removeColor,
    cookieRows,
    useSliders,
    setUseSliders,
    getColorStyle,
    normalizeColorPercentages,
    isMobile,
    goToNextSection,
    activeSection,
    getCanonicalColorName
  } = useProCalculator();

  return (
    <div className="colors-section">
      <div className="section-header">
        <h2 className="section-title">Color Percentages</h2>
      </div>
      {/* Help text container */}
      <div className="section-help-container">
        <SectionHelp sectionId="colors" isMobile={isMobile} />
      </div>
      
      <div className="color-management-row">
        <div className="color-names-container">
          <h3>Colors</h3>
          <div className="color-names-list">
            {colorNames.map((color, index) => (
              <div key={index} className="color-name-item">
                <div className="color-sample" style={getColorStyle(color)}></div>
                <input
                  type="text"
                  value={color}
                  onChange={(e) => {
                    const userInput = e.target.value;
                    const canonicalName = getCanonicalColorName(userInput);
                    const newColorNames = [...colorNames];
                    newColorNames[index] = canonicalName;
                    setColorNames(newColorNames);
                  }}
                  className="color-name-input"
                />
                {colorNames.length > 1 && (
                  <button
                    onClick={() => removeColor(index)}
                    className="remove-color-button"
                    title="Remove Color"
                  >
                    <span className="minus-icon">-</span>
                  </button>
                )}
              </div>
            ))}
            
            <button
              onClick={addColor}
              className="add-color-button"
              title="Add Color"
            >
              <span className="plus-icon">+</span> Add Color
            </button>
          </div>
        </div>
      </div>
      
      <div className="input-type-toggle">
        <label className="slider-toggle-label">
          <input
            type="checkbox"
            checked={useSliders}
            onChange={(e) => setUseSliders(e.target.checked)}
            className="slider-toggle-input"
          />
          <span className="toggle-switch"></span> 
          Use Sliders
        </label>
      </div>

      {/* Normalization Button - Visible only with 3+ colors */} 
      {colorNames.length >= 3 && (
        <div className="normalization-controls">
          <button 
            onClick={normalizeColorPercentages} 
            className="normalize-button"
            title="Adjust percentages in each row to sum to 100%"
          >
            Adjust All Rows to 100%
          </button>
        </div>
      )}
      
      <div className="color-percentages-table">
        <div className="color-percentages-header">
          <div className="cookie-name-header">Cookie</div>
          {colorNames.map((color, index) => (
            <div key={index} className="color-header">
              <div className="color-sample" style={getColorStyle(color)}></div>
              <span>{color}</span>
            </div>
          ))}
        </div>
        
        {colorRows.map((row, rowIndex) => {
          const numColors = colorNames.length;
          let isRowIncomplete = false;

          // Check row sum only if 3 or more colors exist
          if (numColors >= 3) {
            const percentages = row.percentages || [];
            const sum = percentages.reduce((acc, val) => acc + (Number(val) || 0), 0);
            // Use tolerance for floating point
            if (Math.abs(sum - 100) > 0.01) {
              isRowIncomplete = true;
            }
          }

          // Dynamically construct className for the row
          const rowClassName = `color-row ${isRowIncomplete ? 'incomplete-row' : ''}`;

          return (
            <div key={row.id || rowIndex} className={rowClassName}>
              <div className="cookie-name-field">
                {cookieRows.find(c => c.id === row.cookieId)?.cookieName || row.cookieId}
              </div>
              
              {colorNames.map((color, colorIndex) => (
                <div key={colorIndex} className="color-percentage-field">
                  {useSliders ? (
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={row.percentages[colorIndex] || 0}
                      onChange={(e) => updateColorPercentage(row.id, colorIndex, e.target.value)}
                      className="color-slider"
                      style={{ '--percentage': `${row.percentages[colorIndex] || 0}%` }} 
                    />
                  ) : (
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={row.percentages[colorIndex] || 0}
                      onChange={(e) => updateColorPercentage(row.id, colorIndex, e.target.value)}
                      className="color-input"
                    />
                  )}
                  <span className="percentage-label">{row.percentages[colorIndex] || 0}%</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      
      {/* Next section button */}
      {activeSection !== 'all' && (
        <button
          className="next-section-button"
          onClick={() => goToNextSection('colors')}
        >
          Next: Orders
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"></path>
            <path d="M12 5l7 7-7 7"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

export default ColorsSection;
